import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import { Link } from 'react-router-dom';
import { articles, articleCategories } from '@/mocks/articles';
import { useState, useMemo } from 'react';

const articleCoverImages: Record<string, string> = {
  '2025-global-market-trends': 'https://readdy.ai/api/search-image?query=Futuristic%20world%20map%20with%20glowing%20trade%20routes%20and%20rising%20graphs%20modern%20business%20visualization%20warm%20golden%20and%20teal%20tones%20professional&width=600&height=400&seq=art-cover-1&orientation=landscape',
  'cross-border-cooperation-success-case': 'https://readdy.ai/api/search-image?query=Successful%20business%20meeting%20between%20Asian%20and%20Southeast%20Asian%20executives%20in%20modern%20office%20handshake%20moment%20warm%20professional%20setting&width=600&height=400&seq=art-cover-2&orientation=landscape',
  'international-brand-building-guide': 'https://readdy.ai/api/search-image?query=Elegant%20brand%20identity%20presentation%20with%20Chinese%20elements%20merged%20with%20international%20design%20modern%20creative%20studio%20warm%20sophisticated&width=600&height=400&seq=art-cover-3&orientation=landscape',
  'digital-globalization-ecommerce': 'https://readdy.ai/api/search-image?query=Modern%20ecommerce%20fulfillment%20center%20with%20automated%20sorting%20systems%20glowing%20screens%20showing%20global%20orders%20warm%20professional%20lighting&width=600&height=400&seq=art-cover-4&orientation=landscape',
  'overseas-talent-strategy': 'https://readdy.ai/api/search-image?query=Diverse%20international%20team%20collaborating%20in%20modern%20open%20office%20with%20world%20map%20on%20wall%20warm%20natural%20lighting%20professional%20atmosphere&width=600&height=400&seq=art-cover-5&orientation=landscape',
  'global-compliance-risk-management': 'https://readdy.ai/api/search-image?query=Professional%20legal%20documents%20and%20compliance%20checklist%20on%20modern%20desk%20with%20globe%20in%20background%20warm%20sophisticated%20office%20setting&width=600&height=400&seq=art-cover-6&orientation=landscape',
};

export default function ArticlesPage() {
  const [activeCategory, setActiveCategory] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 4;

  const filteredArticles = useMemo(() => {
    if (activeCategory === 'all') return articles;
    return articles.filter((a) => a.category === activeCategory);
  }, [activeCategory]);

  const totalPages = Math.ceil(filteredArticles.length / pageSize);
  const paginatedArticles = filteredArticles.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  return (
    <>
      <SEOHead
        title="新闻洞察"
        description="征途国际新闻洞察，为您提供全球市场趋势分析、跨境合作案例、品牌国际化指南等专业内容，助力企业把握国际化机遇。"
        keywords="征途国际新闻,全球市场趋势,跨境合作案例,品牌国际化,数字全球化"
        canonicalUrl="https://exportr.github.io/articles"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            'name': '征途国际 - 新闻洞察',
            'description': '征途国际新闻洞察，为您提供全球市场趋势分析、跨境合作案例等专业内容。',
            'url': 'https://exportr.github.io/articles',
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            'itemListElement': [
              { '@type': 'ListItem', 'position': 1, 'name': '首页', 'item': 'https://exportr.github.io' },
              { '@type': 'ListItem', 'position': 2, 'name': '新闻洞察', 'item': 'https://exportr.github.io/articles' },
            ],
          },
        ]}
      />

      <Navbar />
      <ScrollToTop />

      <main className="pt-16">
        <section className="bg-background-100/70 py-12 md:py-16">
          <div className="w-full px-6 md:px-10">
            <Breadcrumb items={[{ label: '首页', path: '/' }, { label: '新闻洞察' }]} />

            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-8">
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-3">
                  新闻洞察
                </h1>
                <p className="text-sm text-foreground-600 max-w-xl mx-auto leading-relaxed">
                  全球市场趋势分析、国际化实战经验，前沿洞察一网打尽
                </p>
              </div>

              <div className="flex items-center justify-center flex-wrap gap-2 pb-8">
                {articleCategories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => { setActiveCategory(cat.id); setCurrentPage(1); }}
                    className={`px-4 py-2 text-sm font-medium rounded-full transition-all cursor-pointer whitespace-nowrap ${
                      activeCategory === cat.id
                        ? 'bg-secondary-500 text-background-50'
                        : 'bg-background-50 text-foreground-600 border border-background-200/60 hover:border-secondary-300/40'
                    }`}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
                {paginatedArticles.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/article/${article.slug}`}
                    className="group bg-background-50 border border-background-200/60 rounded-lg overflow-hidden hover:border-primary-300/40 transition-all duration-300 cursor-pointer"
                  >
                    <div className="h-48 overflow-hidden">
                      <img
                        src={articleCoverImages[article.slug]}
                        alt={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-3 flex-wrap mb-3">
                        <span className="text-xs px-2 py-0.5 bg-accent-100/80 text-accent-700 rounded-full font-medium">
                          {article.category}
                        </span>
                        <span className="text-xs text-foreground-400">{article.date}</span>
                        <span className="text-xs text-foreground-400">{article.readTime}</span>
                      </div>
                      <h2 className="text-base font-semibold text-foreground-950 mb-2 font-heading group-hover:text-primary-600 transition-colors line-clamp-2">
                        {article.title}
                      </h2>
                      <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">{article.excerpt}</p>
                    </div>
                  </Link>
                ))}
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="w-9 h-9 flex items-center justify-center rounded-md border border-background-200/60 bg-background-50 text-foreground-600 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-left-s-line"></i>
                    </div>
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`w-9 h-9 flex items-center justify-center rounded-md text-sm font-medium transition-all cursor-pointer ${
                        currentPage === page
                          ? 'bg-primary-500 text-background-50'
                          : 'border border-background-200/60 bg-background-50 text-foreground-600 hover:bg-background-100'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="w-9 h-9 flex items-center justify-center rounded-md border border-background-200/60 bg-background-50 text-foreground-600 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-right-s-line"></i>
                    </div>
                  </button>
                </div>
              )}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}